<script setup lang="ts">
import { computed, inject, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-fomantic-ui'
import { useContainerStore } from '@/stores/containers'
import { useRoomStore } from '@/stores/rooms'
import { ContainerStatus, type Container } from '@/models/Container'
import { ScanTarget } from '@/models/Scan'
import Title from '@/components/Title.vue'
import Scanner from '@/components/Scanner/Scanner.vue'
import FadeTransition from '@/components/FadeTransition.vue'
import ContainerScanHeading from '@/components/Heading/ContainerScanHeading.vue'
import ScaleScanHeading from '@/components/Heading/ScaleScanHeading.vue'

const router = useRouter()
const { toast } = useToast()

const containerStore = useContainerStore()
const roomStore = useRoomStore()

const container = inject('container') as Ref<Container | null>
const sync = inject('sync') as Function
const scanTarget = computed(() => (container.value ? ScanTarget.Scale : ScanTarget.Container))

const containerScanned = (value: string) => {
  container.value = containerStore.findByCode(value)

  if (!container.value && roomStore.findByCode(value)) {
    toast({
      position: 'centered',
      message: `Le code barre scanné n'est pas celui d'un bac.`,
      type: 'warning',
    })
  } else if (!container.value) {
    router.push({ name: 'weigh-container-unknown', params: { code: value } })
  } else if (container.value.status === ContainerStatus.Assigned) {
    router.push({ name: 'weigh-container-assigned' })
  } else if (container.value.status === ContainerStatus.Weighted) {
    router.push({ name: 'weigh-container-weighted' })
  }
}

const scaleScanned = (value: string) => {
  const weight = parseFloat(value.replace(',', '.'))

  if (isNaN(weight) || weight > import.meta.env.VITE_CONTAINER_MAX_WEIGHT) {
    toast({
      position: 'centered',
      message: `La valeur ${value} n'est pas un poids valide.`,
      type: 'warning',
    })
  } else {
    sync(weight)
  }
}

const barcodeScanned = (value: string) => {
  if (scanTarget.value === ScanTarget.Scale) {
    scaleScanned(value)
  } else {
    containerScanned(value)
  }
}
</script>

<template>
  <div class="page weigh-container-scan">
    <FadeTransition>
      <Title>Peser un bac</Title>
    </FadeTransition>
    <FadeTransition>
      <ContainerScanHeading v-if="!container"></ContainerScanHeading>
      <ScaleScanHeading></ScaleScanHeading>
    </FadeTransition>
    <Scanner class="scanner" :target="scanTarget" @barcode-scanned="barcodeScanned"></Scanner>
    <FadeTransition>
      <RouterLink
        class="manuel-weighing"
        :to="{ name: 'weigh-container-manual' }"
        replace
        v-if="container"
      >
        <i class="mdi mdi-pencil"></i>
        <span>Saisir le poids manuellement</span>
      </RouterLink>
    </FadeTransition>
  </div>
</template>

<style scoped lang="scss">
.weigh-container-scan {
  .manuel-weighing {
    text-transform: uppercase;
    display: flex;
    justify-content: center;
    font-weight: 600;
    height: 12.5dvh;
    align-items: center;
    background: none;
    display: block;
    border: none;
    width: 100%;
    display: flex;
    justify-content: center;

    span {
      font-size: min(16px, 4.25vw);
    }

    i.mdi {
      font-size: min(22px, 6vw);
      margin-right: 5px;
      margin-top: -2px;
    }
  }
}
</style>
